import * as React from 'react';
import styled from 'styled-components';
import { desktopSize, mobileSize } from '../../util/variables';
import { Modal, AppText } from '../ui';
import { AnimatedLinkAnchor } from '../ui/AnimatedLinkAnchor';
import { Company } from '../PageSegments/companies/OurCompanies';
import theme from '../../util/theme';
import { RichText } from '../../services';
import { CloseIcon } from '../../svg/Close';

interface CompanyPortfolioModalProps {
  showModal: boolean;
  toggleModal: () => void;
  data: Company;
}

const HeroCompanyModal: React.FunctionComponent<CompanyPortfolioModalProps> = ({
  showModal = false,
  toggleModal,
  data,
}) => {
  React.useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showModal]);

  return (
    <Modal
      height="100%"
      width="100%"
      showModal={showModal}
      toggleModal={toggleModal}
      label="Company Portfolio"
      backgroundColor="#0000007a"
      background="transparent"
    >
      <PortfolioContainer>
        <PortfolioHeader onClick={toggleModal}>
          <CloseIcon />
        </PortfolioHeader>
        <div className="portfolio-div">
          <PortfolioProfileImage src={data.logo_url} />
          <div className="content">
            {data.hero_image_url && (
              <div className="hero-image-container">
                <img src={data.hero_image_url} alt={data.name} />
                {Boolean(data.founders) && (
                  <HiddenTillDesktop>
                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>Founders</ProfileSideBarTitle>
                      <ProfileSideBarText>{data.founders ? data.founders : '-'}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>
                  </HiddenTillDesktop>
                )}
              </div>
            )}
            <HiddenTillDesktop>
              <div className="portfolio-info-column">
                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>Headquarters</ProfileSideBarTitle>
                  <ProfileSideBarText>{data.headquarters}</ProfileSideBarText>
                </ProfileSideBarContentItemContainer>
                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>Founded</ProfileSideBarTitle>
                  <ProfileSideBarText>{data.founded_date}</ProfileSideBarText>
                </ProfileSideBarContentItemContainer>
                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>Invested</ProfileSideBarTitle>
                  <ProfileSideBarText>{data.invested_date}</ProfileSideBarText>
                </ProfileSideBarContentItemContainer>

                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>BUSINESS MODEL</ProfileSideBarTitle>
                  <ProfileSideBarText>{`${data?.business_model || 'N/A'}`}</ProfileSideBarText>
                </ProfileSideBarContentItemContainer>

                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>CUSTOMER</ProfileSideBarTitle>
                  <ProfileSideBarText>{`${data?.customer || 'N/A'}`}</ProfileSideBarText>
                </ProfileSideBarContentItemContainer>

                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>GEO FOCUS</ProfileSideBarTitle>
                  {data?.geo_focus?.map?.(geo => {
                    return <ProfileSideBarText>{geo?.text || 'N/A'}</ProfileSideBarText>;
                  })}
                </ProfileSideBarContentItemContainer>
                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>INDUSTRY</ProfileSideBarTitle>
                  {data?.industry_text?.map?.(industry => {
                    return <ProfileSideBarText>{industry?.text || 'N/A'}</ProfileSideBarText>;
                  })}
                </ProfileSideBarContentItemContainer>
              </div>
            </HiddenTillDesktop>
            <div className="side-content">
              <PortfolioBio>
                {RichText.asText(data.summary_text) ? (
                  <TitleDiv>{RichText.render(data.summary_text)}</TitleDiv>
                ) : (
                  <TitleDiv>
                    <b>{data.name}</b> is automating <b>{data.primary_trend_string}</b> for the{' '}
                    <b>{data.primary_industry_string} industry</b>
                  </TitleDiv>
                )}
                <HiddenTillMobile>
                  <div className="portfolio-info-column">
                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>Headquarters</ProfileSideBarTitle>
                      <ProfileSideBarText>{data.headquarters}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>
                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>Founded</ProfileSideBarTitle>
                      <ProfileSideBarText>{data.founded_date}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>
                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>Invested</ProfileSideBarTitle>
                      <ProfileSideBarText>{data.invested_date}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>

                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>BUSINESS MODEL</ProfileSideBarTitle>
                      <ProfileSideBarText>{`${data?.business_model || 'N/A'}`}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>

                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>CUSTOMER</ProfileSideBarTitle>
                      <ProfileSideBarText>{`${data?.customer || 'N/A'}`}</ProfileSideBarText>
                    </ProfileSideBarContentItemContainer>

                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>GEO FOCUS</ProfileSideBarTitle>
                      {data?.geo_focus?.map?.(geo => {
                        return <ProfileSideBarText>{geo?.text || 'N/A'}</ProfileSideBarText>;
                      })}
                    </ProfileSideBarContentItemContainer>
                    <ProfileSideBarContentItemContainer>
                      <ProfileSideBarTitle>INDUSTRY</ProfileSideBarTitle>
                      {data?.industry_text?.map?.(industry => {
                        return <ProfileSideBarText>{industry?.text || 'N/A'}</ProfileSideBarText>;
                      })}
                    </ProfileSideBarContentItemContainer>
                    {Boolean(data.founders) && (
                      <ProfileSideBarContentItemContainer>
                        <ProfileSideBarTitle>Founders</ProfileSideBarTitle>
                        <ProfileSideBarText>
                          {data.founders ? data.founders : '-'}
                        </ProfileSideBarText>
                      </ProfileSideBarContentItemContainer>
                    )}
                  </div>
                </HiddenTillMobile>
                <ProfileSideBarContentItemContainer>
                  <ProfileSideBarTitle>About</ProfileSideBarTitle>
                  <PortfolioBioText>{data.about}</PortfolioBioText>
                </ProfileSideBarContentItemContainer>
                <PortfolioBioText>
                  <AnimatedLinkAnchor fontSize="20px" color="black" to={data.website}>
                    Visit website
                  </AnimatedLinkAnchor>
                </PortfolioBioText>
              </PortfolioBio>
            </div>
          </div>
        </div>
      </PortfolioContainer>
    </Modal>
  );
};

const HiddenTillMobile = styled.div`
  display: none;
  @media ${mobileSize} {
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 22px;
  }
`;

const HiddenTillDesktop = styled.div`
  display: flex;
  @media ${desktopSize} {
    display: none;
  }
`;

const TitleDiv = styled.div`
  font-family: ${theme.PRIMARY_FONT};
  color: ${theme.BLACK_COLOR};
  font-size: 30px;
  font-weight: 300;
  letter-spacing: -0.75px;
  line-height: 38px;
  margin-bottom: 35px;
  @media ${mobileSize} {
    font-size: 30px;
    line-height: 33.75px;
  }
`;

const ProfileSideBarTitle = styled.div`
  font-family: ${theme.SECONDARY_FONT};
  margin-top: 0;
  font-weight: 400;
  color: rgba(26, 26, 26, 0.6);
  text-transform: uppercase;
  font-size: 12px;
  line-height: 1.16667;
  letter-spacing: 1px;
`;

const ProfileSideBarText = styled.div`
  font-family: ${theme.PRIMARY_FONT};
  color: ${theme.PITCH_BLACK};
  font-weight: 300;
  font-size: 18px;
  @media ${mobileSize} {
    font-size: 16px;
    line-height: 18px;
  }
`;

const ProfileSideBarContentItemContainer = styled.div`
  font-size: 20px;
  line-height: 1.6;
  letter-spacing: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PortfolioContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  padding-top: 7.5rem;
  padding-bottom: 7.5rem;
  background-color: ${theme.WHITE_COLOR};
  border-radius: 20px;
  position: relative;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding-inline: 64px;

  .portfolio-div {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .hero-image-container {
      display: flex;
      flex-direction: column;
      gap: 40px;
      max-width: 320px;

      img {
        width: 320px;
        height: 213px;
        object-fit: cover;
        border-radius: 16px;
      }
    }

    .portfolio-info-column {
      max-width: 160px;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .side-content {
      width: 100%;
      max-width: 500px;
    }
  }

  @media ${mobileSize} {
    width: auto;
    margin-left: 0;
    top: 0px;
    left: 50%;
    transform: translate(-50%, 0);
    border-radius: 0px;
    padding-inline: 24px;

    .content {
      flex-direction: column;
      gap: 24px;

      .hero-image-container {
        max-width: 100%;

        img {
          width: 100%;
          object-fit: cover;
        }
      }

      .portfolio-info-column {
        max-width: 100%;
        width: 100%;
        gap: 24px;
      }
    }
  }
`;

const PortfolioHeader = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: ${theme.PITCH_BLACK};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PortfolioProfileImage = styled.img`
  width: 120px;
`;

const PortfolioBioText = styled(AppText)`
  font-family: ${theme.PRIMARY_FONT};
  color: ${theme.BLACK_COLOR};
  font-size: 16px;
  line-height: 24px;
  font-weight: 300;
  margin-bottom: 42px;
  @media ${mobileSize} {
    font-family: ${theme.PRIMARY_FONT};
    font-size: 16px;
    line-height: 18px;
    font-weight: 400;
    margin-bottom: 30px;
  }
`;

const PortfolioBio = styled.div`
  font-size: 2.25rem;
  @media ${mobileSize} {
    font-size: 1.825rem;
    margin-top: 1.8125rem;
    a {
      font-size: 18px;
    }
  }
`;

export { HeroCompanyModal };
